import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import {
  CheckCircleOutline,
  Close,
  ContentCopy,
  CreateOutlined,
  Delete,
  Download,
  ErrorOutline,
  HourglassEmpty,
  Layers,
  LayersClear,
  Loop,
  Schedule,
  TableChart
} from "@mui/icons-material";
import type { SvgIconComponent } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { useSelectedEntity } from "../../hooks/useSelectedEntity";
import { useLayerActions, useLayerSelector } from "../../store/layer";
import { usePopupActions } from "../../store/popupStore";
import { useSelectionActions, useSelectionSelector } from "../../store/selection";
import { useMapActions } from "../../store/map";
import { deleteSpatialDataset, getSpatialDatasetDownloadUrl, processSpatialDataset } from "../../services/spatialDatasetService";
import { useEntityFormActions } from "../../store/entityForm";
import type { SpatialDataset, SpatialDatasetProcessingStatus } from "../../types";
import { SOURCE_FORMAT_LABEL, SPATIAL_ACTIVE_LAYER_ID } from "../../constants/spatialDataset";
import { toLayerConfig } from "../../hooks/useSpatialDatasetMapLayers";
import { useConfig } from "../../contexts/ConfigContext";
import { REACT_APP_API_ROOT } from "../../utilities/api";
import CsvDataViewer from "./CsvDataViewer";

// ─── Status config ────────────────────────────────────────────────────────────

type ChipColor = "default" | "info" | "success" | "error" | "warning";

interface StatusMeta {
  label: string;
  color: ChipColor;
  Icon: SvgIconComponent;
}

const STATUS_META: Record<SpatialDatasetProcessingStatus, StatusMeta> = {
  pending: { label: "Pending", color: "default", Icon: Schedule },
  uploading: { label: "Uploading…", color: "info", Icon: HourglassEmpty },
  queued: { label: "Queued", color: "warning", Icon: Schedule },
  processing: { label: "Processing…", color: "info", Icon: HourglassEmpty },
  completed: { label: "Ready", color: "success", Icon: CheckCircleOutline },
  failed: { label: "Failed", color: "error", Icon: ErrorOutline }
};

// ─── Helper ───────────────────────────────────────────────────────────────────

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction='row' spacing={1} sx={{ mb: 0.5 }}>
      <Typography variant='caption' color='grey.400' sx={{ minWidth: 80, flexShrink: 0 }}>
        {label}:
      </Typography>
      <Typography variant='caption' color='grey.200' sx={{ wordBreak: "break-all" }}>
        {value}
      </Typography>
    </Stack>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

interface SpatialDatasetDetailProps {
  onClose: () => void;
}

export default function SpatialDatasetDetail({ onClose }: SpatialDatasetDetailProps) {
  const { entity, endpoint, isLoading, refresh, fetchRelationships } = useSelectedEntity();
  // The generic entity adapter (adaptDjangoItem) only preserves id/name/description/etc.
  // in the StandardizedDataItem shape. All SDW-specific fields (processing_status,
  // display_url, bbox, …) survive in _source.rawData — use that as the source of truth.
  const dataset = ((entity as any)?._source?.rawData ?? entity) as unknown as SpatialDataset;

  const config = useConfig();
  // Keep alwaysShowRelated for any future logic; visibility of the Show Related button
  // is now driven by whether the relationship drawer is currently open.
  const { showRelationShip } = useSelectionSelector();

  // Single-active model — the layer id is always SPATIAL_ACTIVE_LAYER_ID (§6e decision)
  const { toggleLayerVisibility, setLayerVisibility, mergeSpatialDatasetLayers } = useLayerActions();
  const { visibleLayers } = useLayerSelector();
  const { closePopup, reset: resetPopup } = usePopupActions();
  const { setSelectedPrimaryStates } = useSelectionActions();
  const { setCommonMapState } = useMapActions();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { openEditForm } = useEntityFormActions();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDataViewer, setShowDataViewer] = useState(false);

  // Track which dataset ID we've already done the initial layer-load + fly-to for,
  // so we don't re-fly on every status-poll tick.
  const initialLoadRef = useRef<number | string | null>(null);

  // Always fetch fresh data on mount so the card doesn't show stale list-row data.
  // Also poll every 5 s while the dataset is still processing.
  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On initial open (including URL deep-link reload), register the layer config and
  // fly to the dataset's bounds — mirroring what SideBar.handleSelectEntity does
  // when the user picks a dataset from the drawer list.
  useEffect(() => {
    if (!dataset?.id || dataset.processing_status !== "completed" || !dataset.display_url) return;
    // Run once per dataset selection (not on every polling refresh).
    if (initialLoadRef.current === dataset.id) return;
    initialLoadRef.current = dataset.id;

    const layerConfig = toLayerConfig(dataset);
    if (layerConfig) {
      mergeSpatialDatasetLayers([layerConfig]);
      setLayerVisibility(SPATIAL_ACTIVE_LAYER_ID, true);
    }
    // Persist the entity ID independently of selectedEntityId (which is cleared when
    // the user switches to a different endpoint). This allows clicking the layer on
    // the map to restore the full spatial dataset selection at any time.
    setCommonMapState({ activeSpatialDatasetEntityId: dataset.id });

    if (dataset.bbox && dataset.bbox.length === 4) {
      const [minLng, minLat, maxLng, maxLat] = dataset.bbox;
      window.dispatchEvent(
        new CustomEvent("culturemap:flyTo", {
          detail: {
            bounds: [
              [minLng, minLat],
              [maxLng, maxLat]
            ],
            clearSpatialLoading: true
          }
        })
      );
    }
  }, [dataset?.id, dataset?.display_url, dataset?.processing_status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const s = dataset?.processing_status;
    if (s === "pending" || s === "queued" || s === "processing") {
      const timer = setInterval(() => refresh(), 5000);
      return () => clearInterval(timer);
    }
  }, [dataset?.processing_status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !dataset) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const layerId = SPATIAL_ACTIVE_LAYER_ID;
  const isVisible = visibleLayers.has(layerId);

  const status: SpatialDatasetProcessingStatus = dataset.processing_status ?? "pending";
  const statusMeta = STATUS_META[status] ?? STATUS_META.pending;
  const { Icon: StatusIcon } = statusMeta;

  const isCompleted = status === "completed";
  const isFailed = status === "failed";

  // ── Tabular (CSV → Parquet / Power BI) ──────────────────────────────────────
  const isTabular = dataset.dataset_type === "tabular";
  // column_schema / row_count are only populated after processing completes.
  const columnSchema = (dataset.stac_properties?.column_schema ?? null) as Record<string, string> | null;
  const columnEntries = columnSchema ? Object.entries(columnSchema) : [];
  const rowCount = dataset.stac_properties?.row_count as number | null | undefined;
  const powerbiPull = config.tabularData?.powerbiPull;
  const powerbiUrl = isTabular && powerbiPull ? `${REACT_APP_API_ROOT.replace(/\/+$/, "")}${powerbiPull}?dataset_id=${dataset.id}` : null;
  // CSV-as-JSON rows endpoint (paginated). Keyed by the raw CSV S3 key, and
  // authenticated with the normal CultureMap login Bearer (not a data-access token).
  const csvDataPath = config.tabularData?.csvData ?? "/api/v2/csv-data/";
  const csvDataUrl =
    isTabular && dataset.original_file_key
      ? `${REACT_APP_API_ROOT.replace(/\/+$/, "")}${csvDataPath}?file_key=${encodeURIComponent(dataset.original_file_key)}&limit=100&offset=0`
      : null;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleToggleVisibility = () => {
    if (isCompleted && dataset.display_url) {
      toggleLayerVisibility(layerId);
    }
  };

  const handleShowRelated = async () => {
    const hasRelated = await fetchRelationships();
    if (!hasRelated) {
      enqueueSnackbar("No related items found", { variant: "info" });
    }
  };

  const handleOpenEdit = () => {
    if (!endpoint || !entity) return;
    // Pass the normalized entity (from useSelectedEntity) rather than the raw dataset
    // (_source.rawData). The raw dataset has un-normalized relationships
    // ({ "org999_place": [...] } flat format, no byTableSlug key), which causes
    // EntityForm to skip its L3 re-fetch (hasRelationships finds media → true)
    // and render an empty relationship section. The normalized entity already
    // has relationships.byTableSlug populated correctly.
    openEditForm(endpoint, entity as any, {
      onUpdated: () => refresh()
    });
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const { url } = await getSpatialDatasetDownloadUrl(dataset.id);
      window.open(url, "_blank", "noreferrer");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyPowerBiUrl = async () => {
    if (!powerbiUrl) return;
    try {
      await navigator.clipboard.writeText(powerbiUrl);
      enqueueSnackbar("Power BI URL copied to clipboard", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy — copy the URL manually", { variant: "error" });
    }
  };

  const handleCopyCsvDataUrl = async () => {
    if (!csvDataUrl) return;
    try {
      await navigator.clipboard.writeText(csvDataUrl);
      enqueueSnackbar("CSV data URL copied to clipboard", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not copy — copy the URL manually", { variant: "error" });
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteSpatialDataset(dataset.id);
      setLayerVisibility(layerId, false);
      setCommonMapState({ activeSpatialDatasetEntityId: null });
      // Clear the current selection after deleting the active dataset.
      setSelectedPrimaryStates({ selectedEntityId: null, showRelationShip: false });
      resetPopup();
      // Invalidate the sidebar list so the deleted row disappears immediately.
      // Covers both the spatial-datasets tab and the synthesized tabular tab.
      await queryClient.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "drawer-data" && (q.queryKey[1] === "spatial-datasets" || q.queryKey[1] === "spatial-datasets-tabular")
      });
      enqueueSnackbar(`"${dataset.name}" deleted successfully`, { variant: "success" });
      onClose();
    } catch {
      enqueueSnackbar("Failed to delete dataset", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await processSpatialDataset(dataset.id, dataset.raster_output ?? undefined);
      onClose();
    } finally {
      setIsRetrying(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        width: 320,
        bgcolor: "#475467",
        borderRadius: "0.5rem",
        overflow: "hidden",
        pointerEvents: "auto",
        padding: 5
      }}
    >
      {/* Header */}
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ px: 5, pt: 5, pb: 5 }}>
        <Stack direction='row' alignItems='center' spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant='subtitle2'
            fontWeight={600}
            color='white'
            sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {dataset.name}
          </Typography>
          <Chip
            label={statusMeta.label}
            color={statusMeta.color}
            size='small'
            icon={<StatusIcon sx={{ fontSize: "0.9rem !important" }} />}
            sx={{ fontWeight: 500, flexShrink: 0 }}
          />
        </Stack>
        <IconButton size='small' onClick={onClose} sx={{ color: "grey.400", ml: 0.5, flexShrink: 0 }}>
          <Close fontSize='small' />
        </IconButton>
      </Stack>

      {/* Description */}
      {dataset.description && (
        <Box sx={{ px: 5, pb: 5 }}>
          <Typography variant='caption' color='grey.400' sx={{ whiteSpace: "pre-wrap" }}>
            {dataset.description}
          </Typography>
        </Box>
      )}

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

      {/* Metadata */}
      <Box sx={{ px: 5, py: 5 }}>
        {dataset.source_format && (
          <MetaRow label='Source format' value={SOURCE_FORMAT_LABEL[dataset.source_format] ?? dataset.source_format} />
        )}
        {dataset.dataset_type && <MetaRow label='Dataset type' value={dataset.dataset_type} />}
        {isCompleted && dataset.display_type && <MetaRow label='Display as' value={dataset.display_type.toUpperCase()} />}
        {isTabular && typeof rowCount === "number" && <MetaRow label='Rows' value={rowCount.toLocaleString()} />}
        {isTabular && columnEntries.length > 0 && <MetaRow label='Columns' value={String(columnEntries.length)} />}
        {dataset.acquisition_date && <MetaRow label='Acquired' value={new Date(dataset.acquisition_date).toLocaleDateString()} />}
      </Box>

      {/* Tabular data — Power BI consumption (no in-app row grid; metadata + handoff only) */}
      {isTabular && (
        <>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{ px: 5, py: 5 }}>
            {!isCompleted ? (
              <Typography variant='caption' color='grey.400'>
                {isFailed
                  ? "Conversion failed — no data available."
                  : "Converting — columns and Power BI URL will appear when processing completes."}
              </Typography>
            ) : (
              <>
                {dataset.original_file_key && (
                  <Button
                    fullWidth
                    size='small'
                    variant='contained'
                    startIcon={<TableChart fontSize='small' />}
                    onClick={() => setShowDataViewer(true)}
                    sx={{
                      mb: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      borderRadius: "0.5rem",
                      bgcolor: "#3b82f6",
                      "&:hover": { bgcolor: "#2563eb" }
                    }}
                  >
                    View data
                  </Button>
                )}
                {columnEntries.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant='caption' color='grey.400' sx={{ display: "block", mb: 0.5 }}>
                      Columns:
                    </Typography>
                    <Stack direction='row' flexWrap='wrap' sx={{ gap: 0.5 }}>
                      {columnEntries.map(([col, dtype]) => (
                        <Tooltip key={col} title={String(dtype)}>
                          <Chip
                            label={col}
                            size='small'
                            sx={{
                              bgcolor: "#667085",
                              color: "#FCFCFD",
                              fontWeight: 500,
                              maxWidth: 140,
                              "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" }
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </Box>
                )}
                <Stack spacing={10} sx={{ m: 10 }}>
                  {powerbiUrl && (
                    <Button
                      fullWidth
                      size='small'
                      startIcon={<ContentCopy fontSize='small' />}
                      onClick={handleCopyPowerBiUrl}
                      sx={{
                        bgcolor: "#667085",
                        color: "#FCFCFD",
                        textTransform: "none",
                        boxShadow: "none",
                        borderRadius: "0.5rem",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.25)" }
                      }}
                    >
                      Copy Power BI URL
                    </Button>
                  )}
                  {csvDataUrl && (
                    <Button
                      fullWidth
                      size='small'
                      startIcon={<ContentCopy fontSize='small' />}
                      onClick={handleCopyCsvDataUrl}
                      sx={{
                        bgcolor: "#667085",
                        color: "#FCFCFD",
                        textTransform: "none",
                        boxShadow: "none",
                        borderRadius: "0.5rem",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.25)" }
                      }}
                    >
                      Copy CSV Data URL
                    </Button>
                  )}
                </Stack>
                {powerbiUrl && (
                  <>
                    <Typography variant='caption' color='white' sx={{ display: "block" }}>
                      In Power BI: Get Data → Web → Advanced, paste this URL and add header{" "}
                      <Box component='span' sx={{ color: "grey.300" }}>
                        Authorization: Bearer &lt;data access token&gt;
                      </Box>
                      .
                    </Typography>
                    <Typography variant='caption' color='grey.100' sx={{ display: "block", mt: 2, fontWeight: 600 }}>
                      NOTE: Needs an organisation data access token — contact your admin to get one.
                    </Typography>
                    <Typography variant='caption' color='grey.100' sx={{ display: "block", mt: 1, fontWeight: 600 }}>
                      Use Download for the raw Parquet file.
                    </Typography>
                  </>
                )}
              </>
            )}
          </Box>
        </>
      )}

      {/* Map visibility toggle */}
      {!isTabular && isCompleted && dataset.display_url && (
        <>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Stack
            direction='row'
            alignItems='center'
            spacing={0.5}
            sx={{ px: 5, py: 5, cursor: "pointer" }}
            onClick={handleToggleVisibility}
          >
            <IconButton size='small' sx={{ color: "white", p: 0.25 }}>
              {isVisible ? <Layers fontSize='small' /> : <LayersClear fontSize='small' />}
            </IconButton>
            <Typography variant='caption' color='white'>
              {isVisible ? "Hide layer" : "Show on map"}
            </Typography>
          </Stack>
        </>
      )}

      {/* Still converting */}
      {!isCompleted && !isFailed && !isTabular && (
        <>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{ px: 5, py: 5 }}>
            <Typography variant='caption' color='grey.400'>
              Converting — layer will appear when processing completes.
            </Typography>
          </Box>
        </>
      )}

      {/* Processing error */}
      {isFailed && dataset.processing_error && (
        <Box sx={{ px: 5, pb: 5 }}>
          <Typography variant='caption' color='error.light' sx={{ display: "block" }}>
            {dataset.processing_error}
          </Typography>
        </Box>
      )}

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

      {/* Actions: Show Related | edit | download | delete */}
      <Stack direction='row' alignItems='center' sx={{ px: 5, py: 5, gap: 0.5 }} flexWrap='wrap'>
        {/* Show Related: visible whenever the relationship drawer is closed so the
             user can always reopen it, even after clicking "Back to grid". */}
        {!isTabular && !showRelationShip && (
          <Button
            size='small'
            onClick={handleShowRelated}
            sx={{
              bgcolor: "#667085",
              color: "#FCFCFD",
              textTransform: "none",
              boxShadow: "none",
              borderRadius: "0.5rem",
              px: "0.75rem",
              py: "0.5rem",
              mr: 0.5,
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" }
            }}
          >
            Show Related
          </Button>
        )}

        {!isTabular && (
          <Tooltip title='Edit'>
            <IconButton size='small' onClick={handleOpenEdit} sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}>
              <CreateOutlined fontSize='small' />
            </IconButton>
          </Tooltip>
        )}

        {isCompleted && (
          <Tooltip title='Download'>
            <span>
              <IconButton
                size='small'
                onClick={handleDownload}
                disabled={isDownloading}
                sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
              >
                {isDownloading ? <CircularProgress size={16} color='inherit' /> : <Download fontSize='small' />}
              </IconButton>
            </span>
          </Tooltip>
        )}

        {isFailed && (
          <Tooltip title='Retry conversion'>
            <span>
              <IconButton
                size='small'
                onClick={handleRetry}
                disabled={isRetrying}
                sx={{ color: "#ed6c02", "&:hover": { bgcolor: "rgba(237,108,2,0.15)" } }}
              >
                {isRetrying ? <CircularProgress size={16} color='inherit' /> : <Loop fontSize='small' />}
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title='Delete'>
          <IconButton
            size='small'
            onClick={() => setShowDeleteDialog(true)}
            sx={{ color: "#f44336", "&:hover": { bgcolor: "rgba(244,67,54,0.15)" } }}
          >
            <Delete fontSize='small' />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onClose={() => !isDeleting && setShowDeleteDialog(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ color: "error.main" }}>⚠️ Permanently Delete {dataset.name}</DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 2 }}>
            <strong>Warning:</strong> You are about to permanently delete this spatial dataset from the database.
          </Typography>
          <Typography variant='body1' sx={{ mt: 2, p: 2, bgcolor: "error.light", color: "error.contrastText", borderRadius: 1 }}>
            This action cannot be undone. Are you absolutely sure you want to delete this item?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={isDeleting} variant='outlined'>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color='error'
            variant='contained'
            startIcon={isDeleting ? <CircularProgress size={20} color='inherit' /> : <Delete />}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Permanently Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Read-only CSV row viewer (tabular datasets) */}
      {isTabular && (
        <CsvDataViewer
          open={showDataViewer}
          onClose={() => setShowDataViewer(false)}
          fileKey={dataset.original_file_key}
          title={dataset.name}
          columns={columnEntries.map(([col]) => col)}
        />
      )}
    </Box>
  );
}
